import React, { useState } from "react";
import {
  Grid,
  InputLabel,
  Collapse,
  Typography,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  Box,
  Button,
  FormControl,
  DialogContent,
  DialogTitle,
  Dialog,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import AddPresetForm from "./AddPresetForm";
import HyperParams from "./HyperParams";
import { deleteData, postData } from "../../utils/api";
import { UserContext } from "../../App";

function AdvancedOptionsSection({
  theme,
  t,
  expanded,
  handleExpandClick,
  selectedModel,
  defaultHyperparamSpaces,
  selectedDefaultParam,
  handleOpenPresetForm,
  showPresetForm,
  modelHyperParams,
  setShowPresetForm,
  setDefaultHyperparamSpaces,
  setSelectedDefaultParam,
  selectedPipelineSteps,
  handleHyperParamsChange,
  handlePipelineStepHyperParamsChange,
  selectedBalancer,
  selectedBalancerOptions,
  handleDataBalancerOptionsChange,
  handleStartPipeline,
  isNewRun,
  fetchTrainConfig,
  setModelHyperParams,
  setSelectedModel,
}) {
  const [presetToDelete, setPresetToDelete] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editPresetName, setEditPresetName] = useState(null);

  const { datasets, currentDatasetIdx, experiments, currentExperimentIdx } =
    React.useContext(UserContext);

  const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
  })(({ expand }) => ({
    transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  const [snackbarSettings, setSnackbarSettings] = useState({
    isOpen: false,
    type: "info",
    message: "",
  });

  const normalizePresetName = (name) =>
    name.trim().toLowerCase().replace(/\s+/g, "_");

  const handleDefaultParamChange = async (e) => {
    const selectedValue = e.target.value;
    setSelectedDefaultParam(selectedValue);

    const { datasetName } = datasets[currentDatasetIdx] || {};
    const { experimentName } = experiments[currentExperimentIdx] || {};

    if (!datasetName || !experimentName) {
      console.error("Missing dataset or experiment name");
      return;
    }

    if (selectedValue === "initial") {
      // Restore initial defaults from backend
      fetchTrainConfig(); // this loads the initial/default run config
      return;
    }

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}/default-hyperparameter`;
    const payload = {
      name_of_default: selectedValue,
    };

    try {
      const response = await postData(url, payload);

      if (response?.status >= 200 && response?.status < 300) {
        const { modelHyperParams: newParams, modelType } = response.data;

        setModelHyperParams((prev) => ({
          ...prev,
          [modelType]: newParams,
        }));

        setSelectedModel(modelType);
      } else {
        console.error("Failed to load preset:", response);
      }
    } catch (err) {
      console.error("Error loading preset:", err);
    }
  };

  const handleAddDefaultHyperparameter = async (presetName, params) => {
    const normalizedName = normalizePresetName(presetName);

    // Check if preset name already exists
    if (defaultHyperparamSpaces.includes(normalizedName)) {
      setSnackbarSettings({
        isOpen: true,
        type: "error",
        message: `Preset name "${presetName}" already exists. Please choose a different name.`,
      });
      // Throw or return early so that add doesn't proceed
      throw new Error("Duplicate preset name");
    }

    const { datasetName } = datasets[currentDatasetIdx];
    const { experimentName } = experiments[currentExperimentIdx];

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}/default-hyperparameterspace`;

    const payload = {
      preset_name: normalizedName,
      hyperParams: params,
    };

    const response = await postData(url, payload);

    if (response.status >= 200 && response.status < 300) {
      setSnackbarSettings({
        isOpen: true,
        type: "success",
        message: `Preset "${presetName}" saved successfully.`,
      });
      return normalizedName;
    }

    setSnackbarSettings({
      isOpen: true,
      type: "error",
      message: `Failed to save preset "${presetName}".`,
    });

    throw new Error(response.detail || "Failed to save preset");
  };

  const handleDeleteDefaultHyperparamerter = async (presetName) => {
    try {
      await deleteData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}/default-hyperparameter/${presetName}`
      ).then(async (response) => {
        if (response.status === 200) {
          setDefaultHyperparamSpaces((prev) =>
            prev.filter((name) => name !== presetName)
          );
          if (selectedDefaultParam === presetName) {
            setSelectedDefaultParam("initial");
            fetchTrainConfig();
          }
        }
      });
    } catch (error) {
      console.error("Failed to delete preset:", error);
    }
  };
  const handleEditDefaultHyperparameter = async (presetName, params) => {
    const normalizedName = normalizePresetName(presetName);
    const { datasetName } = datasets[currentDatasetIdx];
    const { experimentName } = experiments[currentExperimentIdx];

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}/default-hyperparameterspace`;

    const payload = {
      preset_name: normalizedName,
      hyperParams: params,
    };

    const response = await postData(url, payload);

    if (response.status >= 200 && response.status < 300) {
      setSnackbarSettings({
        isOpen: true,
        type: "success",
        message: `Preset "${presetName}" updated successfully.`,
      });
      return normalizedName;
    }

    setSnackbarSettings({
      isOpen: true,
      type: "error",
      message: `Failed to update preset "${presetName}".`,
    });

    throw new Error(response.detail || "Failed to update preset");
  };
  const handleSave = async (presetName, params) => {
    try {
      let normalizedName;

      if (editPresetName) {
        normalizedName = await handleEditDefaultHyperparameter(
          presetName,
          params
        );
      } else {
        normalizedName = await handleAddDefaultHyperparameter(
          presetName,
          params
        );
        setDefaultHyperparamSpaces((prev) => [...prev, normalizedName]);
      }

      setShowPresetForm(false);
      setEditPresetName(null);
      setSelectedDefaultParam(normalizedName);
      handleDefaultParamChange({ target: { value: normalizedName } });
    } catch (err) {
      if (err.message !== "Duplicate preset name") {
        alert("Failed to save preset: ", err.message || err);
      }
    }
  };

  return (
    <Grid container spacing={2} sx={{ marginTop: theme.spacing(2) }}>
      <Grid item xs={12}>
        <InputLabel sx={{ textAlign: "left" }}>
          {t("trainer.AdvancedOptions")}
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="Advanced Options"
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </InputLabel>
      </Grid>

      <Grid item xs={12}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {selectedModel && (
            <>
              {selectedModel === "Neural Network" && (
                <>
                  <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                    <FormControl sx={{ mb: 2, minWidth: 350 }}>
                      <InputLabel id="default-param-select-label">
                        {t("trainer.SelectDefaultHyperparameter")}
                      </InputLabel>
                      <Select
                        labelId="default-param-select-label"
                        value={selectedDefaultParam}
                        label={t("trainer.SelectDefaultHyperparameter")}
                        onChange={handleDefaultParamChange}
                        renderValue={(selected) =>
                          selected === "initial"
                            ? "Initial Defaults"
                            : selected.replace(/_/g, " ")
                        }
                        MenuProps={{
                          PaperProps: {
                            style: { maxHeight: 300 },
                          },
                        }}
                      >
                        <MenuItem value="initial">Initial Defaults</MenuItem>

                        {defaultHyperparamSpaces.map((param) => (
                          <MenuItem
                            key={param}
                            value={param}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography sx={{ flexGrow: 1 }}>
                              {param.replace(/_/g, " ")}
                            </Typography>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowPresetForm(false);
                                setEditPresetName(null);
                                setTimeout(() => {
                                  setEditPresetName(param);
                                  setShowPresetForm(true);
                                }, 0);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setPresetToDelete(param);
                                setConfirmDeleteOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Tooltip title={t("trainer.AddDefaultHyperparameter")}>
                      <IconButton
                        color="primary"
                        onClick={handleOpenPresetForm}
                        sx={{
                          height: 40,
                          width: 40,
                          borderRadius: 20,
                          boxShadow: 5,
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {showPresetForm && (
                    <AddPresetForm
                      initialParams={modelHyperParams[selectedModel]}
                      onCancel={() => {
                        setShowPresetForm(false);
                        setEditPresetName(null);
                      }}
                      onSave={handleSave}
                      presetNameProp={editPresetName || ""}
                      isEditMode={!!editPresetName}
                    />
                  )}
                </>
              )}

              <Typography component="h4" gutterBottom>
                {t("trainer.OptimizationConfigTitle")}
                <Tooltip title={t("trainer.HintOptimizationConfig")}>
                  <IconButton>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Typography>

              <HyperParams
                hyperParams={modelHyperParams[selectedModel]}
                handleChange={handleHyperParamsChange}
                isPartofHPO
              />
            </>
          )}

          {Object.keys(selectedPipelineSteps).map((step, index) => (
            <div key={step}>
              <Grid item xs={12}>
                {index === 0 && (
                  <Typography component="h5" gutterBottom>
                    <br />
                    {t("trainer.HyperParamsPiplineStepsTitle")}
                    <br />
                  </Typography>
                )}
                <HyperParams
                  hyperParams={{
                    [step]: selectedPipelineSteps[step].options,
                  }}
                  handleChange={handlePipelineStepHyperParamsChange}
                  isPartofHPO={false}
                />
              </Grid>
            </div>
          ))}

          {Object.keys(selectedBalancerOptions || {}).length > 0 && (
            <Grid item xs={12}>
              <Typography component="h5" gutterBottom>
                <br />
                {t("trainer.DataBalancingTitle")}
                <br />
              </Typography>
              <HyperParams
                hyperParams={{
                  [selectedBalancer]: selectedBalancerOptions,
                }}
                handleChange={handleDataBalancerOptionsChange}
                isPartofHPO={false}
              />
            </Grid>
          )}
        </Collapse>
      </Grid>

      <Grid item xs={4} sx={{ alignSelf: "end" }}>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleStartPipeline}
          disabled={
            Object.keys(selectedPipelineSteps).length === 0 && !isNewRun
          }
        >
          {t("trainer.StartButton")}
        </Button>
      </Grid>
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {t("trainer.DeleteDefaultHyperparameterWarning")} {presetToDelete}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDeleteDefaultHyperparamerter(presetToDelete);
              setConfirmDeleteOpen(false);
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarSettings.isOpen}
        autoHideDuration={8000}
        onClose={() =>
          setSnackbarSettings((prev) => ({ ...prev, isOpen: false }))
        }
      >
        <Alert
          onClose={() =>
            setSnackbarSettings((prev) => ({ ...prev, isOpen: false }))
          }
          severity={snackbarSettings.type}
          sx={{ width: "100%" }}
        >
          {snackbarSettings.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default AdvancedOptionsSection;
